import React, { useState } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { TransferProofService } from '../../../services/transfer-proof.service';
import { useTranslation } from 'react-i18next';
import styles from './TransferProofUploadModal.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  developmentId: number;
  developmentName: string;
  amount: number;
  currency: 'MXN' | 'USD';
}

export default function TransferProofUploadModal({
  isOpen,
  onClose,
  developmentId,
  developmentName,
  amount,
  currency,
}: Props) {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(selectedFile.type)) {
      addNotification(t('common.invalid_file_type'), 'error');
      return;
    }

    // Validar tamaño (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      addNotification(t('common.file_too_large'), 'error');
      return;
    }

    setFile(selectedFile);

    // Crear preview para imágenes
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      addNotification(t('common.select_file'), 'info');
      return;
    }

    setUploading(true);
    try {
      await TransferProofService.upload({
        developmentId,
        amount,
        currency,
        file,
      });

      addNotification(t('transfer_proofs.upload_success'), 'success');
      setFile(null);
      setPreview(null);
      onClose();
    } catch (error: any) {
      addNotification(error?.message || t('transfer_proofs.upload_error'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setPreview(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button 
          className={styles.closeBtn}
          onClick={handleClose}
          disabled={uploading}
          type="button"
        >
          ✕
        </button>

        <h2 className={styles.title}>{t('transfer_proofs.upload_modal_title')}</h2>
        
        <div className={styles.info}>
          <p><strong>{t('transfer_proofs.development_label')}:</strong> {developmentName}</p>
          <p><strong>{t('transfer_proofs.amount_label')}:</strong> ${amount.toLocaleString()} {currency}</p>
        </div>

        <div className={styles.instructions}>
          <p>📋 {t('transfer_proofs.upload_modal_subtitle')}</p>
          <p>{t('transfer_proofs.file_types_allowed')}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.fileInput}>
            <label htmlFor="file" className={styles.fileLabel}>
              {file ? '📄 ' + t('common.change_file') : '📤 ' + t('transfer_proofs.select_file')}
            </label>
            <input
              type="file"
              id="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
              disabled={uploading}
              className={styles.fileInputHidden}
            />
          </div>

          {file && (
            <div className={styles.filePreview}>
              <p className={styles.fileName}>
                <strong>{t('transfer_proofs.file_label')}:</strong> {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
              {preview && (
                <img src={preview} alt="Preview" className={styles.previewImage} />
              )}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              onClick={handleClose}
              className={styles.btnCancel}
              disabled={uploading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className={styles.btnSubmit}
              disabled={!file || uploading}
            >
              {uploading ? t('transfer_proofs.uploading') : t('transfer_proofs.upload_button')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
